/**
 * ZEN ERP — Dashboard Ana Bileşeni
 * Alt bileşenler dashboard/ klasörüne taşındı:
 *   DashboardOrderModal, DashboardMetrics, DashboardHeroBanner,
 *   DashboardRightPanel, DashboardOrderHistory, DashboardPendingOrders
 */
import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Plus, Truck, CheckCircle2, Send, Wallet, X,
  ShoppingCart, Receipt, FileSignature, Boxes,
  Package, Clock, RefreshCw, FileText, TrendingUp, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { AuthCtx, UICtx } from "../appContexts";
import api from "../api";
import { fmt, ono } from "../appUtils";
import { QuoteBuilderPage } from "./QuoteBuilderPage";
import { QuotePrintModal, BrandSelectModal } from "../quotes";
import { useManualTermins, AlarmModal, ManualTerminModal } from "../reminders";
import { MODULE_GRADIENTS } from "../components/layout/Sidebar";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import CountUp from "../components/anim/CountUp";

gsap.registerPlugin(useGSAP);
import { printHtmlLabelForOrder } from "../utils/pdfUtils";
import { OrderDetailModal } from "./OrdersPage"; // TEK detay modalı (DashboardOrderModal birleştirildi)
import { DashboardMetrics } from "./dashboard/DashboardMetrics";
import { DashboardHeroBanner } from "./dashboard/DashboardHeroBanner";
import { DashboardRightPanel } from "./dashboard/DashboardRightPanel";
import { DashboardOrderHistory } from "./dashboard/DashboardOrderHistory";
import { DashboardPendingOrders } from "./dashboard/DashboardPendingOrders";
import { DashboardMobileOrders } from "./dashboard/DashboardMobileOrders";

// ══════════════════════════════════════════════════════
//  BAYI SAĞ PANELİ — Saat / Kurlar / Son Siparişler
// ══════════════════════════════════════════════════════
function DealerRightPanel({ now, rates, orders, setTab, onRefreshRates }) {
  const STATUS_LABELS = {
    teklif: "Teklif", imalatta: "İmalatta", kargoya_verildi: "Kargo",
    yollandi: "Yollandı", teslim_edildi: "Teslim", iptal: "İptal",
  };
  const STATUS_COLORS = {
    teklif: "bg-slate-400", imalatta: "bg-amber-500", kargoya_verildi: "bg-sky-500",
    yollandi: "bg-orange-500", teslim_edildi: "bg-emerald-500", iptal: "bg-red-500",
  };
  const activeOrders = (orders || []).filter(o => !["teslim_edildi", "iptal"].includes(o.status));
  const recentOrders = (orders || []).slice(0, 6);

  return (
    <aside className="xl:w-72 shrink-0 space-y-4 mt-6 xl:mt-0">
      {/* Saat & Tarih */}
      <div className="zen-panel p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Tarih & Saat</span>
        </div>
        <div className="text-center">
          <div className="zen-mono text-3xl font-black text-slate-800 dark:text-white tabular-nums leading-none">
            {now.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
          <div className="text-[12px] text-slate-500 mt-1">
            {now.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
      </div>

      {/* Döviz Kurları */}
      <div className="zen-panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-slate-400" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Döviz Kurları</span>
          </div>
          <button onClick={onRefreshRates} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400 hover:text-slate-600" title="TCMB'den Güncelle">
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-slate-50 dark:bg-[#0F1626] rounded-lg px-3 py-2">
            <span className="text-[12px] font-bold text-slate-600 dark:text-slate-300">EUR / TL</span>
            <div className="text-right">
              <div className="zen-mono text-[13px] font-black text-emerald-600 dark:text-emerald-400">₺{Number(rates.eurBuy || rates.eurToTl || 0).toFixed(4)}</div>
              <div className="zen-mono text-[11px] text-[#E5322A]">₺{Number(rates.eurSell || rates.eurToTl || 0).toFixed(4)}</div>
            </div>
          </div>
          <div className="flex items-center justify-between bg-slate-50 dark:bg-[#0F1626] rounded-lg px-3 py-2">
            <span className="text-[12px] font-bold text-slate-600 dark:text-slate-300">USD / TL</span>
            <div className="text-right">
              <div className="zen-mono text-[13px] font-black text-emerald-600 dark:text-emerald-400">₺{Number(rates.usdBuy || rates.usdToTl || 0).toFixed(4)}</div>
              <div className="zen-mono text-[11px] text-sky-500">₺{Number(rates.usdSell || rates.usdToTl || 0).toFixed(4)}</div>
            </div>
          </div>
        </div>
        {rates.tcmbDate && rates.tcmbDate !== "-" && (
          <div className="text-[10px] text-slate-400 mt-2 text-center">TCMB+0.50 · {rates.tcmbDate}</div>
        )}
      </div>

      {/* Durum Özeti */}
      <div className="zen-panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-slate-400" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Durum Özeti</span>
          </div>
          <span className="text-[11px] text-slate-400">{activeOrders.length} aktif</span>
        </div>
        <div className="space-y-1.5">
          {Object.entries(STATUS_LABELS).map(([key, label]) => {
            const count = (orders || []).filter(o => o.status === key).length;
            if (!count) return null;
            return (
              <div key={key} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[key]}`} />
                  <span className="text-[12px] text-slate-600 dark:text-slate-300">{label}</span>
                </div>
                <span className="zen-mono text-[12px] font-bold text-slate-700 dark:text-slate-200">{count}</span>
              </div>
            );
          })}
          {!orders.length && <div className="text-[12px] text-slate-400 text-center py-2">Henüz sipariş yok</div>}
        </div>
      </div>

      {/* Son Siparişler */}
      <div className="zen-panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-slate-400" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Son Siparişler</span>
          </div>
          <button onClick={() => setTab("orders")} className="text-[11px] text-blue-500 hover:text-blue-600 flex items-center gap-0.5">
            Tümü <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-1.5">
          {recentOrders.map(o => (
            <div key={o.id} className="flex items-center justify-between py-1 border-b border-slate-100 dark:border-[#1E2D45] last:border-0">
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate zen-mono">#{o.orderNo || o.id?.slice(-6)}</div>
                <div className="text-[10px] text-slate-400 truncate">{o.projectName || "—"}</div>
              </div>
              <div className="text-right shrink-0 ml-2">
                <div className="zen-mono text-[11px] font-bold text-slate-700 dark:text-slate-200">
                  {o.currency}{Number(o.total||0).toFixed(0)}
                </div>
                <div className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status] || "bg-slate-400"} text-white`}>
                  {STATUS_LABELS[o.status] || o.status}
                </div>
              </div>
            </div>
          ))}
          {!recentOrders.length && <div className="text-[12px] text-slate-400 text-center py-2">Henüz sipariş yok</div>}
        </div>
      </div>
    </aside>
  );
}

// ══════════════════════════════════════════════════════
//  BAYİ DASHBOARD — Admin Dashboard gibi tam görünüm
// ══════════════════════════════════════════════════════
function DealerDashboardView({ user, setTab, tr, lang }) {
  // GSAP: istatistik kartları sırayla belirir (hızlı ve ince)
  // GÜVENLİK AĞI: animasyon yarıda kalsa bile içerik 2 sn içinde mutlaka görünür
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.fromTo("[data-anim='stat-card']", { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45, ease: "power2.out", stagger: 0.08, delay: 0.1, clearProps: "transform,opacity" });
    });
    const safety = setTimeout(() => {
      document.querySelectorAll("[data-anim='stat-card']").forEach((el) => {
        el.style.opacity = ""; el.style.transform = "";
      });
    }, 2000);
    return () => { clearTimeout(safety); mm.revert(); };
  });
  const { clockStyle } = useContext(UICtx);
  const [orders, setOrders]         = useState([]);
  const [quotes, setQuotes]         = useState([]);
  const [customer, setCustomer]     = useState(null);
  const [rates, setRates]           = useState({ eurToTl: 38, usdToTl: 34, source: "-", tcmbDate: "-" });
  const [now, setNow]               = useState(new Date());
  const [loading, setLoading]       = useState(true);
  const [showBrandSelect, setShowBrandSelect] = useState(false);
  const [showQuoteModal, setShowQuoteModal]   = useState(false);
  const [quoteBrand, setQuoteBrand]           = useState("zen");
  const [previewQuote, setPreviewQuote]       = useState(null);

  // Saat
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Veri yükle
  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [ordRes, qtRes, custRes, ratesRes] = await Promise.all([
        api.get("/orders").catch(() => ({ data: [] })),
        api.get("/quotes").catch(() => ({ data: [] })),
        api.get("/customers").catch(() => ({ data: [] })),
        api.get("/rates").catch(() => ({ data: {} })),
      ]);
      setOrders(ordRes.data || []);
      setQuotes(qtRes.data || []);
      setCustomer((custRes.data || [])[0] || null);
      if (ratesRes.data?.eurToTl) setRates(ratesRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeOrders  = useMemo(() => orders.filter(o => !["teslim_edildi", "iptal"].includes(o.status)), [orders]);
  const pendingQuotes = useMemo(() => quotes.filter(q => q.stage && !["kazanildi", "kaybedildi"].includes(q.stage)), [quotes]);
  const balance       = customer?.balance || 0;

  // DashboardHeroBanner'ın beklediği summary yapısı
  const summary = useMemo(() => ({
    totalOrders:      orders.length,
    customerCount:    1,
    teklifler:        pendingQuotes,
    imalatta:         orders.filter(o => o.status === "imalatta"),
    kargolanacak:     orders.filter(o => o.status === "kargoya_verildi"),
    yollandi:         orders.filter(o => o.status === "yollandi"),
    odendi:           orders.filter(o => o.status === "teslim_edildi"),
    recentOrders:     orders.slice(0, 5),
    onayBekliyor:     [],
    deadlineWarnings: [],
    brands:           [],
  }), [orders, pendingQuotes]);

  const shortcuts = [
    { key: "teklif",     label: "Teklif Hazırla",   icon: FileSignature, color: "from-violet-600 via-violet-700 to-violet-900", onClick: () => setShowBrandSelect(true) },
    { key: "orders",     label: "Siparişlerim",      icon: ShoppingCart,  color: MODULE_GRADIENTS.orders,                        onClick: () => setTab("orders") },
    { key: "teklifler",  label: "Tekliflerim",       icon: FileText,      color: MODULE_GRADIENTS.quotes,                        onClick: () => setTab("quotes") },
    { key: "ekstre",     label: "Cari Ekstre",       icon: Wallet,        color: "from-amber-500 via-amber-600 to-amber-800",    onClick: () => setTab("customers") },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-sm text-slate-400">Yükleniyor...</div>
    </div>
  );

  return (
    <>
      <div className="xl:flex xl:items-start xl:gap-6 zen-anim-up">
        {/* Sol sütun */}
        <div className="xl:flex-1 min-w-0 space-y-6">
          {/* Hero Banner */}
          <DashboardHeroBanner
            user={user}
            rates={rates}
            summary={summary}
            shortcuts={shortcuts}
            tr={tr}
            lang={lang}
            isAdmin={false}
            setTab={setTab}
            onTeklifHazirla={() => setShowBrandSelect(true)}
          />

          {/* İstatistik kartları */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Aktif Sipariş",      value: <CountUp to={activeOrders.length} duration={1.2} />,                                                             icon: ShoppingCart,  color: "text-blue-500",    bg: "bg-blue-50 dark:bg-blue-900/20"    },
              { label: "Bekleyen Teklif",    value: <CountUp to={pendingQuotes.length} duration={1.2} delay={0.1} />,                                                            icon: FileSignature, color: "text-violet-500",  bg: "bg-violet-50 dark:bg-violet-900/20"},
              { label: "Güncel Bakiye",      value: <CountUp to={Math.abs(Number(balance) || 0)} duration={1.4} delay={0.2} decimals={2} prefix={balance < 0 ? "-€" : "€"} />,                    icon: Wallet,        color: balance > 0 ? "text-rose-500" : "text-emerald-500", bg: "bg-slate-50 dark:bg-[#0F1626]" },
              { label: "Toplam Sipariş",     value: <CountUp to={orders.length} duration={1.3} delay={0.3} />,                                                                   icon: Package,       color: "text-amber-500",   bg: "bg-amber-50 dark:bg-amber-900/20"  },
            ].map(card => (
              <div key={card.label} data-anim="stat-card" className="zen-panel p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${card.bg}`}>
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">{card.label}</div>
                  <div className={`zen-mono text-lg font-black ${card.color}`}>{card.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Son Siparişler Tablosu */}
          {orders.length > 0 && (
            <div className="zen-panel overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-[#1E2D45]">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Son Siparişler</span>
                </div>
                <button onClick={() => setTab("orders")} className="text-[11px] text-blue-500 hover:text-blue-600 flex items-center gap-0.5">
                  Tümünü Gör <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-[#1E2D45]">
                      <th className="text-left px-4 py-2 text-slate-500 font-semibold">Sipariş No</th>
                      <th className="text-left px-4 py-2 text-slate-500 font-semibold">Proje</th>
                      <th className="text-left px-4 py-2 text-slate-500 font-semibold">Durum</th>
                      <th className="text-right px-4 py-2 text-slate-500 font-semibold">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 8).map(o => {
                      const statusColors = { teklif: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300", imalatta: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400", kargoya_verildi: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400", yollandi: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400", teslim_edildi: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400", iptal: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" };
                      const statusLabels = { teklif: "Teklif", imalatta: "İmalatta", kargoya_verildi: "Kargo", yollandi: "Yollandı", teslim_edildi: "Teslim", iptal: "İptal" };
                      return (
                        <tr key={o.id} className="border-b border-slate-50 dark:border-[#1A2640] hover:bg-slate-50 dark:hover:bg-[#0F1626]">
                          <td className="px-4 py-2.5 zen-mono font-bold text-slate-700 dark:text-slate-200">#{o.orderNo || o.id?.slice(-6)}</td>
                          <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 max-w-[160px] truncate">{o.projectName || o.customerName || "—"}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusColors[o.status] || "bg-slate-100 text-slate-600"}`}>
                              {statusLabels[o.status] || o.status}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right zen-mono font-bold text-slate-700 dark:text-slate-200">
                            {o.currency}{Number(o.total || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Sağ panel */}
        <DealerRightPanel
          now={now}
          rates={rates}
          orders={orders}
          setTab={setTab}
          onRefreshRates={() => loadData(true)}
        />
      </div>

      {/* Modaller */}
      {showBrandSelect && (
        <BrandSelectModal
          onClose={() => setShowBrandSelect(false)}
          onSelect={(b) => { setQuoteBrand(b); setShowBrandSelect(false); setShowQuoteModal(true); }}
        />
      )}
      {showQuoteModal && (
        <QuoteBuilderPage
          brand={quoteBrand}
          editingQuote={null}
          onClose={() => setShowQuoteModal(false)}
          onSaved={(q) => { setShowQuoteModal(false); if (q) setPreviewQuote(q); }}
        />
      )}
      {previewQuote && <QuotePrintModal quote={previewQuote} onClose={() => setPreviewQuote(null)} />}
    </>
  );
}


export function Dashboard({ setTab, setEditingOrder }) {
  const { user } = useContext(AuthCtx);
  const { tr, lang, clockStyle } = useContext(UICtx);
  const dateLocale = lang === "en" ? "en-GB" : "tr-TR";
  const isAdmin = user?.role === "admin";
  const isDealer = user?.role === "dealer";

  const [summary, setSummary] = useState(null);
  const [rates, setRates] = useState({ eurToTl: 38, usdToTl: 34, source: "-", tcmbDate: "-" });
  const [ratesError, setRatesError] = useState(false);
  const [now, setNow] = useState(new Date());
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [historyCustomerQ, setHistoryCustomerQ] = useState("");
  const [showBrandSelect, setShowBrandSelect] = useState(false);
  const [quoteBrand, setQuoteBrand] = useState("zen");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [showTerminModal, setShowTerminModal] = useState(false);
  const { termins: manualTermins, reload: reloadTermins } = useManualTermins();
  const [shipModalOrder, setShipModalOrder] = useState(null);
  const [shipTracking, setShipTracking] = useState("");
  const [shipCarrier, setShipCarrier] = useState("");
  const [cargoCompanies, setCargoCompanies] = useState([]);
  const [partialFor, setPartialFor] = useState(null);
  const [partialAmount, setPartialAmount] = useState("");

  // Saat
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  // Hızlı arama
  useEffect(() => {
    const q = searchQ.trim();
    if (!q) { setSearchResults([]); return; }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/orders");
        const lower = q.toLowerCase();
        const matched = (data || []).filter(o =>
          (o.orderNo || "").toLowerCase().includes(lower) ||
          (o.id || "").toLowerCase().includes(lower) ||
          (o.customerName || "").toLowerCase().includes(lower) ||
          (o.projectName || "").toLowerCase().includes(lower)
        ).slice(0, 6);
        setSearchResults(matched);
      } catch (e) { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQ]);

  const load = async (silent = false) => {
    try {
      const [s, r] = await Promise.all([api.get("/dashboard/summary"), api.get("/rates")]);
      setSummary(s.data);
      setRates(r.data);
      if (r.data?.fetchError) setRatesError(true); else setRatesError(false);
    } catch (e) { if (!silent) {} setRatesError(true); }
  };

  useEffect(() => {
    load();
    const rateInterval = setInterval(() => load(true), 10 * 60 * 1000);
    return () => clearInterval(rateInterval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Kargo işlemleri ──
  const markShip = async (o) => {
    try {
      const { data } = await api.get("/settings?category=cargo_companies");
      setCargoCompanies((data || []).filter(s => s.active !== false));
    } catch { setCargoCompanies([]); }
    setShipTracking(""); setShipCarrier("");
    setShipModalOrder(o);
  };
  const confirmShip = async () => {
    if (!shipModalOrder) return;
    await api.post(`/orders/${shipModalOrder.id}/ship`, { trackingNo: shipTracking, carrier: shipCarrier });
    toast.success(`${ono(shipModalOrder)} yollandı olarak işaretlendi`);
    setShipModalOrder(null);
    load();
  };

  // ── Ödeme işlemleri ──
  const requestPayment = (o) => {
    const msg = `Sayın ${o.customerName},\n${ono(o)} nolu siparişiniz kargoya verilmiştir. Ödeme tutarı: ${fmt(o.total - (o.paidAmount || 0), o.currency)}. İyi günler dileriz. - ZEN Perde`;
    navigator.clipboard.writeText(msg);
    toast.success("Ödeme talep mesajı panoya kopyalandı");
  };
  const savePartial = async () => {
    const amt = Number(String(partialAmount).replace(",", "."));
    const remain = (partialFor.total || 0) - (partialFor.paidAmount || 0);
    if (!amt || amt <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    if (amt > remain + 0.01) { toast.error(`Kalan tutardan fazla olamaz (${fmt(remain, partialFor.currency)})`); return; }
    await api.post("/payments", {
      customerId: partialFor.customerId, orderId: partialFor.id, amount: amt,
      currency: partialFor.currency, method: "havale", note: `${ono(partialFor)} kısmi ödeme`,
    });
    toast.success("Kısmi ödeme kaydedildi");
    setPartialFor(null); setPartialAmount("");
    load();
  };
  const markPaid = async (o) => {
    const remain = (o.total || 0) - (o.paidAmount || 0);
    if (!window.confirm(`${o.customerName} - ${fmt(remain, o.currency)} tutar ödeme alındı olarak kaydedilsin mi?`)) return;
    await api.post("/payments", {
      customerId: o.customerId, orderId: o.id, amount: remain,
      currency: o.currency, method: "havale", note: `${ono(o)} tam ödeme`,
    });
    toast.success("Ödeme kaydedildi");
    load();
  };
  const setStatus = async (o, status) => {
    await api.put(`/orders/${o.id}`, { status });
    load();
    if (status === "kargolanacak") {
      toast.success(`${ono(o)} kargoya hazır olarak işaretlendi`, {
        duration: 9000,
        action: { label: "🏷 Kargo Etiketi Yazdır", onClick: () => printHtmlLabelForOrder(o, "kargo") },
      });
    }
  };
  const approveOrder = async (o) => {
    await api.put(`/orders/${o.id}`, { status: "imalatta" });
    toast.success(`${ono(o)} onaylandı → İmalatta`);
    load();
  };

  if (!summary) return <div className="p-6 text-sm text-slate-500">{tr("Yükleniyor...")}</div>;

  // Dealer → kendi özel dashboard'u göster
  if (isDealer) return <DealerDashboardView user={user} setTab={setTab} tr={tr} lang={lang} />;

  const shortcuts = [
    { key: "new-order", label: tr("Yeni Sipariş"),      icon: Plus,          color: "from-[#E5322A] via-[#C42821] to-[#7A140F]",          onClick: () => { setEditingOrder(null); setTab("orders"); setTimeout(() => document.querySelector('[data-testid="new-order-btn"]')?.click(), 200); } },
    { key: "orders",    label: tr("Siparişler"),         icon: ShoppingCart,  color: MODULE_GRADIENTS.orders,                               onClick: () => setTab("orders") },
    { key: "spacer", spacer: true },  // boş yuva — ileride yeni kısayol butonu eklenebilir
    { key: "invoices",  label: tr("Faturalar"),          icon: Receipt,       color: MODULE_GRADIENTS.invoices,                             onClick: () => setTab("invoices") },
    { key: "irsaliye",  label: tr("İrsaliyeler"),        icon: Truck,         color: "from-sky-700 via-sky-800 to-blue-900",                onClick: () => setTab("invoices") },
    { key: "products",  label: tr("Ürünler"),            icon: Boxes,         color: MODULE_GRADIENTS.stock,                                onClick: () => setTab("stock") },
  ];

  return (
    <>
      <div className="xl:flex xl:items-start xl:gap-6 zen-anim-up">
        {/* Sol sütun */}
        <div className="xl:flex-1 min-w-0 space-y-6">
          <DashboardHeroBanner
            user={user}
            rates={rates}
            summary={summary}
            shortcuts={shortcuts}
            tr={tr}
            lang={lang}
            isAdmin={isAdmin}
            setTab={setTab}
            onTeklifHazirla={() => setShowBrandSelect(true)}
          />
          <DashboardMetrics summary={summary} />
          <DashboardMobileOrders
            orders={summary.mobileNewOrders}
            tr={tr}
            setEditingOrder={setEditingOrder}
            onReviewed={(id) => setSummary(s => ({ ...s, mobileNewOrders: (s.mobileNewOrders || []).filter(o => o.id !== id) }))}
          />
          <DashboardPendingOrders
            summary={summary}
            tr={tr}
            setEditingOrder={setEditingOrder}
            onSetStatus={setStatus}
            onMarkShip={markShip}
            onRequestPayment={requestPayment}
            onPartialPay={(o) => { setPartialFor(o); setPartialAmount(""); }}
            onMarkPaid={markPaid}
          />
          <DashboardOrderHistory
            summary={summary}
            tr={tr}
            lang={lang}
            dateLocale={dateLocale}
            historyStatusFilter={historyStatusFilter}
            setHistoryStatusFilter={setHistoryStatusFilter}
            historyCustomerQ={historyCustomerQ}
            setHistoryCustomerQ={setHistoryCustomerQ}
            setViewingOrder={setViewingOrder}
          />
        </div>

        {/* Sağ panel */}
        <DashboardRightPanel
          summary={summary}
          rates={rates}
          ratesError={ratesError}
          tr={tr}
          lang={lang}
          dateLocale={dateLocale}
          clockStyle={clockStyle}
          now={now}
          setTab={setTab}
          setViewingOrder={setViewingOrder}
          user={user}
          manualTermins={manualTermins}
          setShowAlarmModal={setShowAlarmModal}
          setShowTerminModal={setShowTerminModal}
          searchQ={searchQ}
          setSearchQ={setSearchQ}
          searchResults={searchResults}
          searchLoading={searchLoading}
          onApproveOrder={approveOrder}
          onRefreshRates={() => load(true)}
        />
      </div>

      {/* Modaller */}
      {viewingOrder && (
        <OrderDetailModal
          order={viewingOrder}
          onClose={() => setViewingOrder(null)}
          onEdit={() => { setEditingOrder(viewingOrder); setTab("orders"); setViewingOrder(null); }}
          onPayment={(o) => { setViewingOrder(null); setPartialFor(o); setPartialAmount(""); }}
        />
      )}
      {showBrandSelect && (
        <BrandSelectModal
          onClose={() => setShowBrandSelect(false)}
          onSelect={(b) => { setQuoteBrand(b); setShowBrandSelect(false); setShowQuoteModal(true); }}
        />
      )}
      {showQuoteModal && (
        <QuoteBuilderPage
          brand={quoteBrand}
          editingQuote={null}
          onClose={() => setShowQuoteModal(false)}
          onSaved={(q) => { setShowQuoteModal(false); if (q) setPreviewQuote(q); }}
        />
      )}
      {previewQuote && (
        <QuotePrintModal quote={previewQuote} onClose={() => setPreviewQuote(null)} />
      )}
      {showAlarmModal && <AlarmModal onClose={() => setShowAlarmModal(false)} />}
      {showTerminModal && <ManualTerminModal onClose={() => setShowTerminModal(false)} onSaved={reloadTermins} />}

      {/* Kargo Gönderim Modalı */}
      {shipModalOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShipModalOrder(null)}>
          <div className="zen-panel w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="ship-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-sky-500" />
                <h3 className="text-base font-bold">Kargo Bilgileri</h3>
              </div>
              <button onClick={() => setShipModalOrder(null)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-[#22304A]"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-500 mb-4 bg-slate-100 dark:bg-[#0F1626] rounded-lg p-2">
              <span className="font-bold text-[#E5322A]">{ono(shipModalOrder)}</span> · {shipModalOrder.customerName}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 block mb-1">Kargo Takip No (opsiyonel)</label>
                <input
                  data-testid="ship-tracking-input"
                  autoFocus
                  value={shipTracking}
                  onChange={(e) => setShipTracking(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") confirmShip(); }}
                  placeholder="Takip numarası girin…"
                  className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-[#E5322A]"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 block mb-1">Kargo Firması</label>
                {cargoCompanies.length > 0 ? (
                  <select
                    data-testid="ship-carrier-select"
                    value={shipCarrier}
                    onChange={(e) => setShipCarrier(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-[#E5322A]"
                  >
                    <option value="">Firma seçin veya aşağıya yazın…</option>
                    {cargoCompanies.map(c => (
                      <option key={c.id || c.key} value={c.value || c.label || c.key}>{c.value || c.label || c.key}</option>
                    ))}
                  </select>
                ) : null}
                <input
                  data-testid="ship-carrier-input"
                  value={shipCarrier}
                  onChange={(e) => setShipCarrier(e.target.value)}
                  placeholder={cargoCompanies.length > 0 ? "Veya firma adı yazın…" : "Kargo firması adı…"}
                  className={`w-full ${cargoCompanies.length > 0 ? "mt-2" : ""} rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-[#E5322A]`}
                />
                {cargoCompanies.length === 0 && (
                  <p className="text-[10px] text-slate-400 mt-1">Ayarlar &gt; Kargo Firmaları'ndan firma ekleyebilirsiniz.</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="ship-confirm-btn" onClick={confirmShip} className="zen-btn-primary flex-1 !text-xs"><Truck className="w-3.5 h-3.5" /> Yollandı Olarak İşaretle</button>
              <button onClick={() => setShipModalOrder(null)} className="zen-btn-ghost !text-xs">Vazgeç</button>
            </div>
          </div>
        </div>
      )}

      {/* Kısmi Ödeme Modalı */}
      {partialFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPartialFor(null)}>
          <div className="zen-panel w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="partial-payment-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold">Kısmi Ödeme</h3>
              <button data-testid="partial-payment-close" onClick={() => setPartialFor(null)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-[#22304A]"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              <span className="zen-mono font-bold text-[#E5322A]">{ono(partialFor)}</span> · {partialFor.customerName}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] mb-4">
              <div className="rounded-lg bg-slate-100 dark:bg-[#0F1626] p-2">
                <div className="text-slate-500 dark:text-slate-400">Toplam</div>
                <div className="zen-mono font-bold text-sm">{fmt(partialFor.total, partialFor.currency)}</div>
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-[#0F1626] p-2">
                <div className="text-slate-500 dark:text-slate-400">Kalan</div>
                <div className="zen-mono font-bold text-sm text-amber-500">{fmt((partialFor.total || 0) - (partialFor.paidAmount || 0), partialFor.currency)}</div>
              </div>
            </div>
            <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Alınan Tutar ({partialFor.currency})</label>
            <input
              data-testid="partial-payment-amount"
              autoFocus
              value={partialAmount}
              onChange={(e) => setPartialAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") savePartial(); }}
              placeholder="0,00"
              className="w-full mt-1 rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm zen-mono outline-none focus:border-[#E5322A]"
            />
            <div className="flex gap-2 mt-4">
              <button data-testid="partial-payment-save" onClick={savePartial} className="zen-btn-primary flex-1 !text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Kaydet</button>
              <button data-testid="partial-payment-cancel" onClick={() => setPartialFor(null)} className="zen-btn-ghost !text-xs">Vazgeç</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
